import React, { useState } from 'react';
import { Calendar, Clock, Star, Play, Video, Target } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext.js';

const MonthlyPackageCard = React.memo(({ package: monthlyPackage, onViewPackage }) => {
  const { subscription } = useSubscription();
  const [isHovered, setIsHovered] = useState(false);

  const userTier = subscription?.tier || 'basic';
  const hasAccess = monthlyPackage.availableTiers.includes(userTier);

  const getTierBadgeColor = () => {
    if (userTier === 'elite') return 'bg-purple-100 text-purple-800 border-purple-200';
    if (userTier === 'premium') return 'bg-blue-100 text-blue-800 border-blue-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getTierIcon = () => {
    if (userTier === 'elite') return '��';
    if (userTier === 'premium') return '⭐';
    return '📋';
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <div
      className="bg-white rounded-lg shadow-lg overflow-hidden transform transition-all duration-300 hover:scale-105 cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onViewPackage(monthlyPackage._id)}
    >
      <div className="relative">
        <img
          src={monthlyPackage.thumbnailImage}
          alt={monthlyPackage.title}
          className="w-full h-48 object-cover"
        />
        
        {/* Overlay with theme */}
        <div className="absolute inset-0 bg-black bg-opacity-30 flex items-end">
          <div className="p-4 text-white">
            <h3 className="text-lg font-bold">{monthlyPackage.theme}</h3>
            <p className="text-sm opacity-90">
              Month {monthlyPackage.subscriptionMonth}
            </p>
          </div>
        </div>

        {/* Tier Badge */}
        <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-medium border ${getTierBadgeColor()}`}>
          <span className="mr-1">{getTierIcon()}</span>
          {userTier.charAt(0).toUpperCase() + userTier.slice(1)}
        </div>

        {/* Access Badge */}
        <div className="absolute top-3 left-3">
          {hasAccess ? (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
              <span className="mr-1">✓</span>
              Accessible
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
              <span className="mr-1">🔒</span>
              Upgrade Required
            </span>
          )}
        </div>

        {/* Play Button Overlay */}
        {isHovered && hasAccess && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <Play className="w-16 h-16 text-white opacity-80" />
          </div>
        )}
      </div>

      <div className="p-5">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {monthlyPackage.title}
        </h3>
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {monthlyPackage.description}
        </p>

        <div className="flex items-center text-gray-500 text-sm mb-4">
          <Clock className="w-4 h-4 mr-1" />
          <span>{formatDuration(monthlyPackage.estimatedDuration)}</span>
          <span className="mx-2">•</span>
          <Star className="w-4 h-4 mr-1" />
          <span>{monthlyPackage.difficulty}</span>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {monthlyPackage.availableTiers.map(tier => (
            <span
              key={tier}
              className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                tier === 'elite' ? 'bg-purple-100 text-purple-800' :
                tier === 'premium' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}
            >
              {tier.charAt(0).toUpperCase() + tier.slice(1)}
            </span>
          ))}
        </div>

        {/* Daily Challenge Preview */}
        {monthlyPackage.dailyChallenge && monthlyPackage.dailyChallenge.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h4 className="text-md font-semibold text-gray-800 mb-2 flex items-center">
              <Target className="w-4 h-4 mr-2 text-green-500" />
              Daily Challenge Preview:
            </h4>
            <ul className="space-y-1 text-sm text-gray-600">
              {monthlyPackage.dailyChallenge.slice(0, 3).map(exercise => (
                <li key={exercise.day} className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-green-500" />
                  Day {exercise.day}: {exercise.title}
                </li>
              ))}
              {monthlyPackage.dailyChallenge.length > 3 && (
                <li className="text-gray-500">...and {monthlyPackage.dailyChallenge.length - 3} more days!</li>
              )}
            </ul>
          </div>
        )}

        {/* Workout of the Week Preview */}
        {monthlyPackage.workoutOfTheWeek && monthlyPackage.workoutOfTheWeek.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h4 className="text-md font-semibold text-gray-800 mb-2 flex items-center">
              <Video className="w-4 h-4 mr-2 text-blue-500" />
              Workout of the Week Preview:
            </h4>
            <ul className="space-y-1 text-sm text-gray-600">
              {monthlyPackage.workoutOfTheWeek.slice(0, 2).map(workout => (
                <li key={workout.week} className="flex items-center">
                  <Video className="w-4 h-4 mr-2 text-blue-500" />
                  Week {workout.week}: {workout.title}
                </li>
              ))}
              {monthlyPackage.workoutOfTheWeek.length > 2 && (
                <li className="text-gray-500">...and {monthlyPackage.workoutOfTheWeek.length - 2} more!</li>
              )}
            </ul>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center text-gray-500 text-sm">
            <Calendar className="w-4 h-4 mr-1" />
            <span>Month {monthlyPackage.subscriptionMonth}</span>
          </div>
          
          <button
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              hasAccess
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            disabled={!hasAccess}
          >
            {hasAccess ? 'View Package' : 'Upgrade to Access'}
          </button>
        </div>
      </div>
    </div>
  );
});

export default MonthlyPackageCard;